import { useNavigate, useParams } from "react-router-dom";
import {
    BundleTypes,
    DiscountTypes,
    isValidLink,
    nonEmptyOverride,
    RedirectOptionsTypes,
    updateBundleConfigWithRealTimeProducts,
    validateBBTierDiscounts,
    validateFontSize,
    validateFreeGiftStep,
    validateHexalColor,
    validateSteps,
    WidgetPositionsTypes,
    WidgetShowOptionsTypes,
} from "../constants";
import PlaceholderContentForm from "./PlaceholderContentForm";
import styles from "./Placeholder.module.css";
import PlaceholderDesignForm from "./PlaceholderDesignForm";
import PlaceholderSettingsForm from "./PlaceholderSettingsForm";
import PlaceholderTargetForm from "./PlaceholderTargetForm";
import usePlaceholderHook, { initialConfigData } from "./usePlaceholderHook";
import { endPoints, getData } from "../../../utilities/networkCalls";
import { createContext, Dispatch, useContext, useEffect, useRef, useState } from "react";
import { getBundleRoute, MAX_LENGTH_DESKTOP, MAX_LENGTH_MOBILE, migratePlaceholderConfig } from "../../constants";
import { FormError, useField, useForm } from "@shopify/react-form";
import {
    Action,
    ChangeEvents,
    ProductObj,
    FreeGiftStepType,
    PlaceholderConfigInterface,
    PlaceholderStepsType,
} from "../Types";
import { AppConfigContext } from "../../../context/AppConfigContext";
import { getUpdatedTargeting, validateTargeting } from "../../shouldRenderTargeting";
import useBundleMixpanelHook from "../../../hooks/useBundleMixpanelHook";
import FormPreviewWrapper2 from "../../../components/FormPreviewWrapper2/FormPreviewWrapper2";
import { scrollToTop } from "../../../utilities/functions";
import { PlaceholderPreview } from "./PlaceholderBundle/PlaceholderBundle";
import { useInspectorMode } from "../../../hooks/useInspectorMode";
import TemplateSelectionScreen from "../../../components/TemplateSelectionScreen/TemplateSelectionScreen";

export interface PlaceholderContextType {
    bundleConfig?: PlaceholderConfigInterface;
    dispatchBundleConfig: Dispatch<Action<PlaceholderConfigInterface>>;
    handleNext: (step: number, stepName: string) => void;
    fields: any;
    submit: (event?: React.FormEvent) => Promise<void>;
    reset: () => void;
    validate: () => FormError[];
    makeClean: () => void;
    isMobile: boolean;
    SwatchesBundleConfigTouchedRef: React.MutableRefObject<Record<string, string>>;
    // --- Inspector mode additions ---
    inspectorMode?: boolean;
    setInspectorMode?: Dispatch<React.SetStateAction<boolean>>;
    inspectorTarget: any | null;
}

export interface BundleAsProductType {
    product_title: string;
    product_description: string;
    images: ImageType[]; // Use the defined ImageType here
}

export interface ImageType {
    originalSource: string; // URL of the image
    alt: string; // Alt text for the image
    mediaContentType: "IMAGE"; // To specify the type
}

export const PlaceholderContext = createContext<PlaceholderContextType>(null as any);

const Placeholder = () => {
    const { bundleConfig, dispatchBundleConfig } = usePlaceholderHook();
    const allow_free_gifts_ref = useRef(bundleConfig.allow_free_gifts);
    allow_free_gifts_ref.current = bundleConfig.allow_free_gifts;
    const [_config, setConfig] = useState({
        ...bundleConfig,
    });
    const { trackFromTabs } = useBundleMixpanelHook();
    const isProductPageVisibleRef = useRef(bundleConfig.isProductPageVisible);
    isProductPageVisibleRef.current = bundleConfig.isProductPageVisible;
    const SwatchesBundleConfigTouchedRef = useRef<Record<string, string>>({});
    const tierDiscountBasisRef = useRef(bundleConfig.tier_discount_basis);
    tierDiscountBasisRef.current = bundleConfig.tier_discount_basis;

    const {
        fields,
        submit,
        // submitting,
        dirty,
        reset,
        submitErrors,
        makeClean,
        validate,
    } = useForm({
        fields: {
            render_embed_on_visibility: useField(_config?.render_embed_on_visibility),
            isProductPageVisible: useField(_config?.isProductPageVisible),
            hide_native_elements: useField(_config?.hide_native_elements || ""),
            bundle_as_product: {
                product_title: useField({
                    value: _config?.bundle_as_product?.product_title || "",
                    validates: (val) => {
                        if (isProductPageVisibleRef.current) {
                            if (!val) {
                                return "Bundle Product Title is required";
                            }
                            if (val.length < 3) {
                                return "Bundle Product Title must be at least 3 characters long";
                            }
                        }
                        return undefined;
                    },
                }),
                product_description: useField(_config?.bundle_as_product?.product_description || ""),
                images: useField(_config?.bundle_as_product?.images || []),
                show_on_product_page: useField(_config?.bundle_as_product?.show_on_product_page || []),
                product_handle: useField(_config?.bundle_as_product?.product_handle || ""),
                double_discount: useField(_config?.bundle_as_product?.double_discount || false),
                collections: useField(_config?.bundle_as_product?.collections || []),
            },
            hide_out_of_stock_products: useField(!!_config?.hide_out_of_stock_products),
            show_search_bar: useField(!!_config?.show_search_bar),
            allow_pre_selected_products: useField(_config?.allow_pre_selected_products),
            pre_selected_products: useField(_config?.pre_selected_products),
            make_pre_selected_compulsory: useField(_config?.make_pre_selected_compulsory),
            product_card_desktop_size: useField(_config?.product_card_desktop_size),
            product_card_mobile_size: useField(_config?.product_card_mobile_size),
            allow_conditional_logic: useField(_config?.allow_conditional_logic),
            product_conditions: useField(_config?.product_conditions),
            redirect_options: useField(_config?.redirect_options),
            show_popup_on_cart_redirect: useField(_config?.show_popup_on_cart_redirect),
            popup_text: useField(_config?.popup_text),
            popup_redirect_url: useField({
                value: _config?.popup_redirect_url,
                validates: [
                    (val) => {
                        if (!val) return undefined;
                        const isValid = isValidLink(val);
                        return isValid ? undefined : "Popup Redirect Link is not valid.";
                    },
                ],
            }),
            selected_template_placeholder: useField(_config?.selected_template_placeholder),
            move_selected_products_first: useField(_config?.move_selected_products_first || false),
            accordion_product_layout: useField(_config?.accordion_product_layout || "grid"),

            limit_max_quantity_per_product: useField(!!_config?.limit_max_quantity_per_product),
            max_quantity_per_product: useField<string | number>({
                value: _config?.max_quantity_per_product,
                validates: [(val) => nonEmptyOverride(val as string, "Max Quantity per Product is required")],
            }),
            title: useField({
                value: _config?.title,
                validates: [(val) => nonEmptyOverride(val, "Title is required")],
            }),
            offer_name: useField(_config?.offer_name),
            description: useField(_config?.description),
            bundle_product_level: useField<"product" | "variant">(_config?.bundle_product_level),
            steps: useField<PlaceholderStepsType[]>({
                value: _config?.steps,
                validates: [(val: any) => validateSteps(val)],
            }),
            default_step: useField<PlaceholderStepsType>(_config?.default_step),
            free_gift_step: useField<FreeGiftStepType | undefined>({
                value: _config?.free_gift_step,
                validates: [(val: any) => (val ? validateFreeGiftStep(val) : undefined)],
            }),
            products: useField({
                value: _config?.products,
                validates: [],
            }),
            discount_type: useField<DiscountTypes>({
                value: _config?.discount_type,
                validates: [(val) => nonEmptyOverride(val, "Type is required")],
            }),
            discount_value: useField({
                value: _config?.discount_value as unknown as string,
                validates: [(val) => nonEmptyOverride(val, "Discount value is required")],
            }),
            tier_discount_basis: useField(_config?.tier_discount_basis),
            discount_options: useField({
                value: _config?.discount_options,
                validates: [(val) => validateBBTierDiscounts(val, null, tierDiscountBasisRef?.current)],
                dirtyStateComparator: (v1, v2) => {
                    console._log("v1111111", v1, "v2", v2);
                    return JSON.stringify(v1) != JSON.stringify(v2);
                },
            }),
            show_progress_bar: useField(!!_config?.show_progress_bar),
            hide_prices_product_card: useField(!!_config?.hide_prices_product_card),
            order_discounts: useField(_config?.order_discounts || false),
            product_discounts: useField(_config?.product_discounts || false),
            shipping_discounts: useField(_config?.shipping_discounts || false),
            add_to_cart_cta_text: useField({
                value: _config?.add_to_cart_cta_text,
                validates: [(val) => nonEmptyOverride(val, "CTA value is required")],
            }),
            add_to_cart_cta_hover_text: useField({
                value: _config?.add_to_cart_cta_hover_text,
                validates: [(val) => nonEmptyOverride(val, "CTA hover text is required")],
            }),
            quantity_text: useField({
                value: _config?.quantity_text,
                validates: [(val) => nonEmptyOverride(val, "Quantity text is required")],
            }),
            footer_total_text: useField({
                value: _config?.footer_total_text,
                validates: [(val) => nonEmptyOverride(val, "Footer total value is required")],
            }),
            footer_text: useField({
                value: _config?.footer_text,
                validates: [(val) => nonEmptyOverride(val, "Footer text value is required")],
            }),
            free_text: useField({
                value: _config?.free_text,
                validates: [(val) => nonEmptyOverride(val, "Free text value is required")],
            }),
            buy_now_cta_text: useField({
                value: _config?.buy_now_cta_text,
                validates: [(val) => nonEmptyOverride(val, "Buy Now CTA text is required")],
            }),
            edit_cta_text: useField({
                value: _config?.edit_cta_text,
                validates: [(val) => nonEmptyOverride(val, "Edit bundle CTA text is required")],
            }),
            bundle_ready_text: useField({
                value: _config?.bundle_ready_text,
                validates: [(val) => nonEmptyOverride(val, "Bundle ready text is required")],
            }),
            next_cta_text: useField({
                value: _config?.next_cta_text,
                validates: [(val) => nonEmptyOverride(val, "Next CTA text value is required")],
            }),
            previous_cta_text: useField({
                value: _config?.previous_cta_text,
                validates: [(val) => nonEmptyOverride(val, "Previous CTA text value is required")],
            }),
            checkout_cta_text: useField({
                value: _config?.checkout_cta_text,
                validates: [(val) => nonEmptyOverride(val, "Checkout CTA text value is required")],
            }),
            done_cta_text: useField({
                value: _config?.done_cta_text,
                validates: [(val) => nonEmptyOverride(val, "Done CTA text value is required")],
            }),
            out_of_stock_text: useField({
                value: _config?.out_of_stock_text,
                validates: [(val) => nonEmptyOverride(val, "Out of stock value is required")],
            }),
            order_summary_title: useField({
                value: _config?.order_summary_title,
                validates: [(val) => nonEmptyOverride(val, "Order summary title text value is required")],
            }),
            order_summary_subtotal: useField({
                value: _config?.order_summary_subtotal,
                validates: [(val) => nonEmptyOverride(val, "Order summary subtotal text value is required")],
            }),
            order_summary_discount: useField({
                value: _config?.order_summary_discount,
                validates: [(val) => nonEmptyOverride(val, "Order summary discount text value is required")],
            }),
            start_date: useField(_config?.start_date),
            end_date: useField(_config?.end_date || null),
            use_compare_at_price: useField(_config?.use_compare_at_price || false),
            apply_discount_on_compare_price: useField(_config?.apply_discount_on_compare_price || false),
            no_close_accordian_after_completion: useField(_config?.no_close_accordian_after_completion || false),
            allow_free_gifts: useField(_config?.allow_free_gifts),
            free_gifts: useField({
                value: _config?.free_gifts,
                validates: [
                    (fg: ProductObj[]) => {
                        console._log(allow_free_gifts_ref.current);
                        if (allow_free_gifts_ref.current) {
                            if (fg.length <= 0) {
                                return "Free gift is required";
                            }

                            for (const p of fg) {
                                if (p.quantity < 0) {
                                    return "Free gift quantity cannot be negative";
                                }
                                if (p.quantity === 0) {
                                    return "Free gift quantity cannot be zero";
                                }
                            }
                        }
                        return undefined;
                    },
                ],
            }),
            free_gift_text: useField({
                value: _config?.free_gift_text,
                validates: [(val) => nonEmptyOverride(val, "Free gift Title is required")],
            }),
            bundle_embed_id: useField(_config?.bundle_embed_id),
            show_swatches: useField(_config?.show_swatches),
            swatchesProductsMap: useField({
                value: _config?.swatchesProductsMap,
                validates: [],
                dirtyStateComparator: (v1, v2) => {
                    const res = JSON.stringify(v1) != JSON.stringify(v2);
                    return res;
                },
            }),
            swatchOptions: useField({
                value: _config?.swatchOptions,
                validates: [],
                dirtyStateComparator: (v1, v2) => {
                    return JSON.stringify(v1) != JSON.stringify(v2);
                },
            }),
            swatchShape: useField(_config?.swatchShape),
            swatchSize: useField(_config?.swatchSize),

            offer_cta_text: useField({
                value: _config?.offer_cta_text,
                validates: [(val) => nonEmptyOverride(val, "Upsell button text is required")],
            }),
            limit_show_offer_cta: useField<"all" | "products" | "collections" | "none">(_config?.limit_show_offer_cta),
            show_offer_cta_pages: useField(_config?.show_offer_cta_pages),
            offer_cta_position: useField({
                value: _config?.offer_cta_position,
                validates: [(val) => nonEmptyOverride(val, "Offer CTA text is required")],
            }),
            bundles_design: {
                default_cta: useField<boolean>(_config?.bundles_design?.default_cta),
                bundle_title_color: useField({
                    value: _config?.bundles_design?.bundle_title_color,
                    validates: [(val) => validateHexalColor(val, "Bundle title")],
                }),
                bundle_title_style: useField(_config?.bundles_design?.bundle_title_style),
                bundle_title_size: useField({
                    value: _config?.bundles_design?.bundle_title_size as unknown as number,
                    validates: [(val) => validateFontSize(val, "Bundle title font size")],
                }),
                bundle_widget_bg: useField({
                    value: _config?.bundles_design?.bundle_widget_bg as unknown as string,
                    validates: [(val) => validateHexalColor(val, "Bundle widget")],
                }),
                // bundle_widget_bg_opacity: useField({
                //     value: _config?.bundles_design?.bundle_widget_bg_opacity as unknown as string,
                //     validates: [
                //         (val) =>
                //             validateFontSize(val, "Bundle widget", {
                //                 min: 0,
                //                 max: 1,
                //             }),
                //     ],
                // }),
                bundle_widget_font_family: useField(_config?.bundles_design?.bundle_widget_font_family),
                product_background_color: useField({
                    value: _config?.bundles_design?.product_background_color,
                    validates: [(val) => validateHexalColor(val, "Product background")],
                }),
                product_border_color: useField({
                    value: _config?.bundles_design?.product_border_color,
                    validates: [(val) => validateHexalColor(val, "Product border")],
                }),

                product_name_color: useField({
                    value: _config?.bundles_design?.product_name_color,
                    validates: [(val) => validateHexalColor(val, "Product name")],
                }),
                product_name_style: useField(_config?.bundles_design?.product_name_style),
                product_name_size: useField({
                    value: _config?.bundles_design?.product_name_size as unknown as string,
                    validates: [(val) => validateFontSize(val, "Product name font")],
                }),
                product_price_color: useField({
                    value: _config?.bundles_design?.product_price_color,
                    validates: [(val) => validateHexalColor(val, "Product price")],
                }),
                product_price_style: useField(_config?.bundles_design?.product_price_style),
                product_price_size: useField({
                    value: _config?.bundles_design?.product_price_size as unknown as string,
                    validates: [(val) => validateFontSize(val, "Product price font")],
                }),
                variant_selector_text_size: useField({
                    value: (_config?.bundles_design?.variant_selector_text_size as unknown as string) ?? 12,
                    validates: [(val) => validateFontSize(val, "Variant Selector size")],
                }),
                variant_selector_text_color: useField({
                    value: _config?.bundles_design?.variant_selector_text_color ?? "#383838",
                    validates: [(val) => validateHexalColor(val, "Variant Selector")],
                }),
                variant_selector_text_style: useField(
                    _config?.bundles_design?.variant_selector_text_style ?? "450 normal",
                ),
                bb_margin_top: useField(_config?.bundles_design?.bb_margin_top ?? "0"),
                is_product_img_height_auto: useField(!!_config?.bundles_design?.is_product_img_height_auto),
                product_img_size: useField(_config?.bundles_design?.product_img_size),
                product_custom_img_size: useField(_config?.bundles_design?.product_custom_img_size || 160),
                product_custom_img_size_mobile: useField(
                    _config?.bundles_design?.product_custom_img_size_mobile || 160,
                ),
                product_img_show: useField(_config?.bundles_design?.product_img_show),
                product_img_border_radius: useField(_config?.bundles_design?.product_img_border_radius),

                border_radius: useField(_config?.bundles_design?.border_radius as unknown as string),
                border_color: useField({
                    value: _config?.bundles_design?.border_color,
                    validates: [(val) => validateHexalColor(val, "Product border")],
                }),
                border_width: useField({
                    value: _config?.bundles_design?.border_width as unknown as string,
                    validates: [
                        (val) =>
                            validateFontSize(val, "Border width font", {
                                min: 0,
                                max: 15,
                            }),
                    ],
                }),
                background_color: useField({
                    value: _config?.bundles_design?.background_color,
                    validates: [(val) => validateHexalColor(val, "Product background")],
                }),
                highlighted_offer_tag_color: useField({
                    value: _config?.bundles_design?.highlighted_offer_tag_color,
                    validates: [(val) => validateHexalColor(val, "Highlighted offer tag")],
                }),
                highlighted_offer_tag_style: useField(_config?.bundles_design?.highlighted_offer_tag_style),
                highlighted_offer_tag_size: useField({
                    value: _config?.bundles_design?.highlighted_offer_tag_size as unknown as string,
                    validates: [(val) => validateFontSize(val, "Offer tag font")],
                }),
                highlighted_price_tag_color: useField({
                    value: _config?.bundles_design?.highlighted_price_tag_color,
                    validates: [(val) => validateHexalColor(val, "Highlighted price tag")],
                }),
                highlighted_price_tag_style: useField(_config?.bundles_design?.highlighted_price_tag_style),
                highlighted_price_tag_size: useField({
                    value: _config?.bundles_design?.highlighted_price_tag_size as unknown as string,
                    validates: [(val) => validateFontSize(val, "Price tag font")],
                }),
                total_footer_text_color: useField({
                    value: _config?.bundles_design?.total_footer_text_color,
                    validates: [(val) => validateHexalColor(val, "Bottom bar text")],
                }),
                total_footer_text_style: useField(_config?.bundles_design?.total_footer_text_style),
                total_footer_text_size: useField({
                    value: _config?.bundles_design?.total_footer_text_size as unknown as string,
                    validates: [(val) => validateFontSize(val, "Total footer text font")],
                }),
                total_footer_price_text_color: useField({
                    value: _config?.bundles_design?.total_footer_price_text_color,
                    validates: [(val) => validateHexalColor(val, "Total footer price text")],
                }),
                total_footer_price_text_style: useField(_config?.bundles_design?.total_footer_price_text_style),
                total_footer_price_text_size: useField({
                    value: _config?.bundles_design?.total_footer_price_text_size as unknown as string,
                    validates: [(val) => validateFontSize(val, "Total footer price font")],
                }),
                total_footer_background_color: useField({
                    value: _config?.bundles_design?.total_footer_background_color,
                    validates: [(val) => validateHexalColor(val, "Bottom bar background")],
                }),
                total_footer_background_style: useField(_config?.bundles_design?.total_footer_background_style),
                add_to_cart_bg_color: useField({
                    value: _config?.bundles_design?.add_to_cart_bg_color,
                    validates: [(val) => validateHexalColor(val, "Add to cart background")],
                }),
                edit_bundle_cta_bg_color: useField({
                    value: _config?.bundles_design?.edit_bundle_cta_bg_color,
                    validates: [(val) => validateHexalColor(val, "Edit bundle CTA background")],
                }),
                edit_bundle_cta_text_color: useField({
                    value: _config?.bundles_design?.edit_bundle_cta_text_color,
                    validates: [(val) => validateHexalColor(val, "Edit bundle text")],
                }),
                edit_bundle_cta_style: useField(_config?.bundles_design?.edit_bundle_cta_style),
                edit_bundle_cta_size: useField({
                    value: _config?.bundles_design?.edit_bundle_cta_size as unknown as number,
                    validates: [(val) => validateFontSize(val, "Edit bundle CTA font size")],
                }),
                add_to_cart_border_radius: useField(
                    _config?.bundles_design?.add_to_cart_border_radius as unknown as string,
                ),
                add_to_cart_border_color: useField({
                    value: _config?.bundles_design?.add_to_cart_border_color,
                    validates: [(val) => validateHexalColor(val, "Add to cart border")],
                }),
                add_to_cart_text_color: useField({
                    value: _config?.bundles_design?.add_to_cart_text_color,
                    validates: [(val) => validateHexalColor(val, "Add to cart text")],
                }),
                add_to_cart_text_size: useField({
                    value: _config?.bundles_design?.add_to_cart_text_size as unknown as string,
                    validates: [(val) => validateFontSize(val, "Add to cart text font")],
                }),
                add_to_cart_text_style: useField(_config?.bundles_design?.add_to_cart_text_style),
                free_gift_header_bg_color: useField({
                    value: _config?.bundles_design?.free_gift_header_bg_color,
                    validates: [(val) => validateHexalColor(val, "Free gift header background")],
                }),
                free_gift_header_color: useField({
                    value: _config?.bundles_design?.free_gift_header_color,
                    validates: [(val) => validateHexalColor(val, "Free gift header text")],
                }),

                free_gift_header_size: useField({
                    value: _config?.bundles_design?.free_gift_header_size,
                    validates: [(val) => validateFontSize(val, "Free gift header text font")],
                }),
                free_gift_header_style: useField(_config?.bundles_design?.free_gift_header_style),
                free_gift_bg_color: useField({
                    value: _config?.bundles_design?.free_gift_bg_color,
                    validates: [(val) => validateHexalColor(val, "Free gift background")],
                }),
                free_gift_text_color: useField({
                    value: _config?.bundles_design?.free_gift_text_color,
                    validates: [(val) => validateHexalColor(val, "Free gift text")],
                }),
                free_gift_text_size: useField({
                    value: _config?.bundles_design?.free_gift_text_size,
                    validates: [(val) => validateFontSize(val, "Free gift text font")],
                }),
                free_gift_text_style: useField(_config?.bundles_design?.free_gift_text_style),
                free_gift_border: useField<boolean>(_config?.bundles_design?.free_gift_border),
                free_gift_border_width: useField({
                    value: _config?.bundles_design?.free_gift_border_width,
                    validates: [(val) => validateFontSize(val, "Free gift border width", { min: 0, max: 10 })],
                }),
                free_gift_border_color: useField({
                    value: _config?.bundles_design?.free_gift_border_color,
                    validates: [(val) => validateHexalColor(val, "Free gift border")],
                }),
                product_img_border_width: useField({
                    value: _config?.bundles_design?.product_img_border_width,
                    validates: [(val) => validateFontSize(val, "Product image border width", { min: 0, max: 10 })],
                }),
                product_img_border_color: useField({
                    value: _config?.bundles_design?.product_img_border_color,
                    validates: [(val) => validateHexalColor(val, "Product image border")],
                }),
                secondary_cta_bg_color: useField({
                    value: _config?.bundles_design?.secondary_cta_bg_color,
                    validates: [(val) => validateHexalColor(val, "Next button background")],
                }),
                secondary_cta_text_color: useField({
                    value: _config?.bundles_design?.secondary_cta_text_color,
                    validates: [(val) => validateHexalColor(val, "Next button text")],
                }),
                tertiary_cta_bg_color: useField({
                    value: _config?.bundles_design?.tertiary_cta_bg_color,
                    validates: [(val) => validateHexalColor(val, "Previous button background")],
                }),
                tertiary_cta_text_color: useField({
                    value: _config?.bundles_design?.tertiary_cta_text_color,
                    validates: [(val) => validateHexalColor(val, "Previous button text")],
                }),
                step_label_color: useField({
                    value: _config?.bundles_design?.step_label_color,
                    validates: [(val) => validateHexalColor(val, "Step label")],
                }),
                step_label_size: useField({
                    value: _config?.bundles_design?.step_label_size,
                    validates: [(val) => validateFontSize(val, "Step label")],
                }),
                step_label_style: useField(_config?.bundles_design?.step_label_style),
                step_page_title_color: useField({
                    value: _config?.bundles_design?.step_page_title_color,
                    validates: [(val) => validateHexalColor(val, "Step page title")],
                }),
                step_page_title_style: useField(_config?.bundles_design?.step_page_title_style),
                step_page_title_size: useField({
                    value: _config?.bundles_design?.step_page_title_size as unknown as number,
                    validates: [(val) => validateFontSize(val, "Step page title font size")],
                }),

                step_page_subtitle_color: useField({
                    value:
                        _config?.bundles_design?.step_page_subtitle_color ||
                        initialConfigData?.bundles_design?.step_page_subtitle_color,
                    validates: [(val) => validateHexalColor(val, "Step page subtitle")],
                }),
                step_page_subtitle_style: useField(
                    _config?.bundles_design?.step_page_subtitle_style ||
                        initialConfigData?.bundles_design?.step_page_subtitle_style,
                ),
                step_page_subtitle_size: useField({
                    value:
                        (_config?.bundles_design?.step_page_subtitle_size as unknown as number) ||
                        initialConfigData?.bundles_design?.step_page_subtitle_size,
                    validates: [(val) => validateFontSize(val, "Step page subtitle font size")],
                }),
                order_summary_bg_color: useField({
                    value: _config?.bundles_design?.order_summary_bg_color,
                    validates: [(val) => validateHexalColor(val, "Order summary background")],
                }),
                order_summary_header_text_color: useField({
                    value: _config?.bundles_design?.order_summary_header_text_color,
                    validates: [(val) => validateHexalColor(val, "Order summary header text")],
                }),
                order_summary_header_text_size: useField({
                    value: _config?.bundles_design?.order_summary_header_text_size,
                    validates: [(val) => validateFontSize(val, "Order summary header text")],
                }),
                order_summary_header_text_style: useField(_config?.bundles_design?.order_summary_header_text_style),
                order_summary_rows_text_color: useField({
                    value: _config?.bundles_design?.order_summary_rows_text_color,
                    validates: [(val) => validateHexalColor(val, "Order summary rows text")],
                }),
                order_summary_rows_text_size: useField({
                    value: _config?.bundles_design?.order_summary_rows_text_size,
                    validates: [(val) => validateFontSize(val, "Order summary rows text")],
                }),
                order_summary_rows_text_style: useField(_config?.bundles_design?.order_summary_rows_text_style),
                popup_cta_bg_color: useField({
                    value: _config?.bundles_design?.popup_cta_bg_color,
                    validates: [(val) => validateHexalColor(val, "Popup CTA background")],
                }),
                special_text_color: useField({
                    value: _config?.bundles_design.special_text_color,
                    validates: [(val) => validateHexalColor(val, "Special text")],
                }),
                special_text_background_color: useField({
                    value: _config?.bundles_design.special_text_background_color,
                    validates: [(val) => validateHexalColor(val, "Special text background")],
                }),
                special_text_font_size: useField({
                    value: _config?.bundles_design.special_text_font_size as unknown as string,
                    validates: [(val) => validateFontSize(val, "Special text font")],
                }),
                special_text_style: useField(_config?.bundles_design.special_text_style),

                added_quantity_bg_color: useField({
                    value: _config?.bundles_design.added_quantity_bg_color,
                    validates: [(val) => validateHexalColor(val, "Quantity label background")],
                }),
                added_quantity_text_color: useField({
                    value: _config?.bundles_design.added_quantity_text_color,
                    validates: [(val) => validateHexalColor(val, "Quantity label text")],
                }),
                added_quantity_text_style: useField(_config?.bundles_design.added_quantity_text_style),
                added_quantity_text_size: useField({
                    value: _config?.bundles_design.added_quantity_text_size as unknown as string,
                    validates: [(val) => validateFontSize(val, "Quantity label text font")],
                }),
                modal_footer_bg: useField({
                    value: _config?.bundles_design.modal_footer_bg,
                    validates: [(val) => validateHexalColor(val, "Popup footer")],
                }),
                dotted_border_color: useField({
                    value: _config?.bundles_design.dotted_border_color,
                    validates: [(val) => validateHexalColor(val, "Dotted border color")],
                }),
                step_container_bg: useField({
                    value: _config?.bundles_design.step_container_bg,
                    validates: [(val) => validateHexalColor(val, "Step container background")],
                }),
            },
            general_settings: {
                show_bundle_on_product_page: useField(_config?.general_settings?.show_bundle_on_product_page),
                show_multiple_bundles_on_product_page: useField(
                    _config?.general_settings?.show_multiple_bundles_on_product_page,
                ),
                redirect_after_adding_product_to_cart: useField<RedirectOptionsTypes>(
                    _config?.general_settings?.redirect_after_adding_product_to_cart || "",
                ),
                redirect_to_checkout: useField<boolean | undefined>(_config?.general_settings?.redirect_to_checkout),
                show_powered_by_kefi: useField<boolean | undefined>(_config?.general_settings?.show_powered_by_kefi),
                max_width: useField(_config?.general_settings?.max_width),
                override_max_width: useField(_config?.general_settings?.override_max_width),
            },
            bundles_settings: {
                enable_stock_control: useField(_config?.bundles_settings?.enable_stock_control),
                out_of_stock_text: useField({
                    value: _config?.bundles_settings?.out_of_stock_text as string,
                    validates: [(val) => nonEmptyOverride(val, "Text is required")],
                }),
                listing_variant_with_image: useField(_config?.bundles_settings?.listing_variant_with_image),
                work_only_with_widget: useField(_config?.bundles_settings?.work_only_with_widget),
                bundle_position: useField<WidgetPositionsTypes>(
                    _config?.bundles_settings?.bundle_position as WidgetPositionsTypes,
                ),
                custom_selector: useField<string>(_config?.bundles_settings?.custom_selector || ""),
            },

            show_bundle_widget: useField<WidgetShowOptionsTypes>(_config?.show_bundle_widget),
            free_shipping: useField(_config?.free_shipping),
            animation_add_to_cart_btn: useField(_config?.animation_add_to_cart_btn),

            targeting: useField({
                value: _config.targeting,
                validates: [validateTargeting],
                dirtyStateComparator: (v1, v2) => {
                    return JSON.stringify(v1) != JSON.stringify(v2);
                },
            }),
        },
        onSubmit: async () => {
            return { status: "success" };
        },
    });

    const [selected, setSelected] = useState(0);
    const [fetchingBundle, setFetchingBundle] = useState(true);
    const [isMobile, setIsMobile] = useState(false);
    const maxLength = isMobile ? MAX_LENGTH_MOBILE : MAX_LENGTH_DESKTOP;
    const { bId } = useParams();
    const goTo = useNavigate();
    const appConfigContext = useContext(AppConfigContext);
    const { sft, globalSettingsInfo } = appConfigContext;
    const { globalSettings, loading } = globalSettingsInfo;

    console._log("bundle id", bId, setIsMobile, maxLength);
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => {
            window.removeEventListener("resize", checkMobile);
        };
    }, []);

    useEffect(() => {
        if (!loading) {
            if (bId) {
                fetchBundle(bId);
            } else {
                setFetchingBundle(false);
                dispatchBundleConfig({
                    type: ChangeEvents.initState,
                    // @ts-expect-error required
                    init: {
                        bundles_design: {
                            ...bundleConfig?.bundles_design,
                            ...globalSettings?.design_settings_bb_placeholder,
                        } as PlaceholderConfigInterface["bundles_design"],
                        general_settings: {
                            ...bundleConfig?.general_settings,
                            // eslint-disable-next-line no-prototype-builtins
                            ...(globalSettings?.hasOwnProperty("general_settings")
                                ? {
                                      ...globalSettings?.general_settings,
                                      show_powered_by_kefi: globalSettings?.general_settings?.hasOwnProperty(
                                          "show_powered_by_kefi",
                                      )
                                          ? globalSettings?.general_settings?.show_powered_by_kefi
                                          : true,
                                  }
                                : {}),
                        },
                        bundles_settings: {
                            ...bundleConfig?.bundles_settings,
                            ...globalSettings?.bundle_settings_bb_placeholder,
                        } as PlaceholderConfigInterface["bundles_settings"],
                    },
                });
                setConfig((ps: any) => ({
                    ...bundleConfig,
                    bundles_design: {
                        ...ps.bundles_design,
                        ...globalSettings?.design_settings_bb_placeholder,
                    } as PlaceholderConfigInterface["bundles_design"],
                    general_settings: {
                        ...ps.general_settings,
                        // eslint-disable-next-line no-prototype-builtins
                        ...(globalSettings?.hasOwnProperty("general_settings")
                            ? {
                                  ...globalSettings?.general_settings,
                                  show_powered_by_kefi: globalSettings?.general_settings?.hasOwnProperty(
                                      "show_powered_by_kefi",
                                  )
                                      ? globalSettings?.general_settings?.show_powered_by_kefi
                                      : true,
                              }
                            : {}),
                    },
                    bundles_settings: {
                        ...ps.bundles_settings,
                        ...globalSettings?.bundle_settings_bb_placeholder,
                    } as PlaceholderConfigInterface["bundles_settings"],
                }));
            }
        }
    }, [globalSettings, loading, bId]);

    useEffect(() => {
        if (bundleConfig?.form_id) {
            goTo(getBundleRoute(BundleTypes.BB_PLACEHOLDER, bundleConfig?.form_id));
        }
    }, [bundleConfig?.form_id]);

    const fetchBundle = async (id: string | null = "") => {
        if (!id) {
            shopify.saveBar.hide("save-bar");
            goTo(getBundleRoute(BundleTypes.BB_PLACEHOLDER));
        }
        setFetchingBundle(true);
        const res = await getData({ url: endPoints.getBundle + id });
        if (res.status == 200) {
            if (res?.data?.bundle?.form_id) {
                const migratedBundle = migratePlaceholderConfig(res.data.bundle);
                const newConfig = await updateBundleConfigWithRealTimeProducts(sft, [migratedBundle]);
                const _c = {
                    ...bundleConfig,
                    ...migratedBundle,
                    products: newConfig[0].products,
                    publish_count: res.data.publish_count,
                    selected_template_placeholder: res?.data?.bundle?.selected_template_placeholder || "normal",
                };
                _c.bundles_design = {
                    ...bundleConfig?.bundles_design,
                    ..._c.bundles_design,
                    product_custom_img_size_mobile:
                        _c?.bundles_design?.product_custom_img_size_mobile ??
                        _c?.bundles_design?.product_custom_img_size,
                };

                if (_c?.steps?.length) {
                    _c.steps = _c?.steps.map((step: any) => ({
                        ...step,
                        show_step_conditions: step.hasOwnProperty("show_step_conditions")
                            ? step.show_step_conditions
                            : true,
                        enable_subSteps: step.hasOwnProperty("enable_subSteps") ? step.enable_subSteps : false,
                        subSteps: step.hasOwnProperty("subSteps") ? step.subSteps : [],
                    }));
                }
                _c.general_settings = {
                    ...bundleConfig?.general_settings,
                    ..._c.general_settings,
                    show_powered_by_kefi: _c?.general_settings?.hasOwnProperty("show_powered_by_kefi")
                        ? _c?.general_settings?.show_powered_by_kefi
                        : true,
                    redirect_to_checkout: _c?.general_settings?.redirect_to_checkout ?? false,
                };
                _c.targeting = { ..._c.targeting, ...getUpdatedTargeting(_c.targeting) };
                dispatchBundleConfig({
                    type: ChangeEvents.initState,
                    init: {
                        ..._c,
                    },
                });
                setConfig({
                    ..._c,
                });
                setFetchingBundle(false);
                reset();
            } else {
                shopify.saveBar.hide("save-bar");
                goTo(getBundleRoute(BundleTypes.BB_PLACEHOLDER));
            }
        } else {
            shopify.saveBar.hide("save-bar");
            goTo(getBundleRoute(BundleTypes.BB_PLACEHOLDER));
        }
    };

    // console._log("bundle id", bId, setIsMobile, maxLength);
    console._log("---_config------", _config, bundleConfig);

    const handleNext = async (num: number, screen = "-") => {
        submit();
        if (!validate().length) {
            trackFromTabs({
                cta: "Form Tabs Switched",
                tabName: screen,
                bundleType: bundleConfig?.bundle_type,
            });
            setSelected(num);
        } else {
            scrollToTop();
        }
    };
    const [previewType, setPreviewType] = useState<null | number>(1);

    // --- Inspector mode -----
    const [inspectorMode, setInspectorMode] = useState(false);
    const hoveredElRef = useRef<HTMLElement | null>(null);
    const [selectedEl, setSelectedEl] = useState<HTMLElement | null>(null);
    const [inspectorTarget, setInspectorTarget] = useState<any>(null);
    const previewRef = useRef<HTMLDivElement>(null);

    const { handleInspectorClick } = useInspectorMode({
        inspectorMode,
        previewRef,
        selectedEl,
        setSelectedEl,
        hoveredElRef,
        setInspectorTarget,
        handleNext,
        bundleConfig,
    });

    return (
        <PlaceholderContext.Provider
            value={{
                bundleConfig,
                dispatchBundleConfig,
                handleNext,
                fields,
                submit,
                reset,
                makeClean,
                validate,
                isMobile,
                SwatchesBundleConfigTouchedRef,
                // --- inspector mode additions ---
                inspectorMode,
                setInspectorMode,
                inspectorTarget,
            }}
        >
            {loading || fetchingBundle || bundleConfig?.selected_template_placeholder || bundleConfig?.form_id ? (
                <FormPreviewWrapper2
                    oldBundleConfig={_config}
                    resetForm={reset}
                    setSelected={setSelected}
                    bundleType={BundleTypes.BB_PLACEHOLDER}
                    renderForms={[
                        <PlaceholderContentForm />,
                        <PlaceholderDesignForm />,
                        <PlaceholderTargetForm />,
                        <PlaceholderSettingsForm />,
                    ]}
                    renderPreview={
                        <div
                            ref={previewRef}
                            style={{ userSelect: inspectorMode ? "none" : "text" }}
                            id="previewRefContainerId"
                            onClickCapture={handleInspectorClick}
                            className={`${styles.previewContainer} ${inspectorMode ? styles.inspectorActive : ""}`}
                        >
                            <PlaceholderPreview
                                appConfigContext={appConfigContext}
                                bundleConfig={{
                                    ...bundleConfig,
                                    SwatchesBundleConfigTouchedRef: SwatchesBundleConfigTouchedRef,
                                }}
                                previewType={previewType}
                                targetForm={null}
                                inspectorMode={inspectorMode}
                            />
                        </div>
                    }
                    globalErrors={submitErrors}
                    submitForm={submit}
                    globalDirty={dirty}
                    dispatchBundleConfig={dispatchBundleConfig}
                    selected={selected}
                    handleNext={handleNext}
                    bundleConfig={bundleConfig}
                    setConfig={setConfig}
                    validateForm={validate}
                    makeCleanForm={makeClean}
                    fetchBundle={fetchBundle}
                    loading={fetchingBundle || loading}
                    inspectorMode={inspectorMode}
                    setInspectorMode={setInspectorMode}
                    setPreviewType={setPreviewType}
                />
            ) : (
                <TemplateSelectionScreen
                    bundleConfig={bundleConfig}
                    dispatchBundleConfig={dispatchBundleConfig}
                    makeClean={makeClean}
                    setConfig={setConfig}
                />
            )}
        </PlaceholderContext.Provider>
    );
};

export default Placeholder;
